(() => {
  const STORAGE_KEY = 'kickis:favorites:v1';
  const VISITOR_KEY = 'kickis:favorites:visitor:v1';
  // Bump the cache namespace whenever the count payload contract changes.
  // This prevents malformed values written by an older theme version from
  // being reused for every card until their original expiry time.
  const COUNT_CACHE_KEY = 'kickis:favorite-counts:v2';
  const COUNT_CACHE_TTL_MS = 2 * 60 * 1000;
  const EVENT_NAME = 'kickis:favorites:change';

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const readContext = () => {
    const element = document.getElementById('KickisFavoritesContext');
    if (!element?.textContent?.trim()) return {};
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('Kunde inte läsa favoritkontext.', error);
      return {};
    }
  };

  const itemKey = (item) => {
    const type = item.type || item.itemType || 'product';
    const rawId = String(item.id || item.itemKey || item.handle || item.url || '');
    return rawId.startsWith(`${type}:`) ? rawId : `${type}:${rawId}`;
  };

  const getVisitorId = () => {
    try {
      let visitorId = window.localStorage.getItem(VISITOR_KEY);
      if (!visitorId) {
        visitorId = window.crypto?.randomUUID?.() || `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        window.localStorage.setItem(VISITOR_KEY, visitorId);
      }
      return visitorId;
    } catch (error) {
      return '';
    }
  };

  const toCount = (value) => {
    const count = Number(value);
    return Number.isFinite(count) && count >= 0 ? Math.round(count) : null;
  };

  const formatCount = (count) => count === 1 ? '1 favorit' : `${count} favoriter`;

  const normaliseItem = (item = {}) => {
    const type = item.type || item.itemType || 'product';
    const handle = item.handle || '';
    const rawId = String(item.id || item.itemKey || handle || item.url || '');
    const id = rawId.startsWith(`${type}:`) ? rawId.slice(type.length + 1) : rawId;
    let url = item.url || '';

    if (!url && type === 'product' && handle) {
      url = `/products/${handle}`;
    } else if (type === 'pattern' && id) {
      const legacyPatternUrl = /\/pages\/monster(?:\?|$)/.test(url);
      if (!url || legacyPatternUrl) {
        const routeRoot = window.Shopify?.routes?.root || '/';
        url = `${routeRoot}pages/monster/${encodeURIComponent(id)}`;
      }
    }

    return {
      type,
      id,
      handle,
      url,
      title: item.title || 'Favorit',
      image: item.image || item.imageUrl || '',
      vendor: item.vendor || '',
      price: item.price || '',
      compareAtPrice: item.compareAtPrice || '',
      badges: Array.isArray(item.badges) ? item.badges.filter(Boolean) : [],
      addedAt: item.addedAt || item.updatedAt || new Date().toISOString(),
    };
  };

  const readStoredItems = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      const rawItems = Array.isArray(parsed) ? parsed : Object.values(parsed || {});
      return rawItems.map(normaliseItem).filter((item) => item.id || item.url);
    } catch (error) {
      console.warn('Kunde inte läsa favoriter från webbläsaren.', error);
      return [];
    }
  };

  const writeStoredItems = (items) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  };

  const mergeItems = (...groups) => {
    const byKey = new Map();
    groups.flat().filter(Boolean).map(normaliseItem).forEach((item) => {
      byKey.set(itemKey(item), { ...byKey.get(itemKey(item)), ...item });
    });
    return Array.from(byKey.values()).sort((a, b) => String(b.addedAt).localeCompare(String(a.addedAt)));
  };

  class KickisFavorites {
    constructor() {
      this.context = readContext();
      this.syncEndpoint = String(this.context.accountSyncEndpoint || this.context.syncEndpoint || '').trim();
      this.syncWritable = Boolean(this.context.customerId && this.context.accountSyncWritable && this.syncEndpoint);
      this.favoriteCountsEnabled = Boolean(this.context.favoriteCountsEnabled);
      this.favoriteCountsEndpoint = String(this.context.favoriteCountsEndpoint || '').trim();
      this.favoriteEventsEndpoint = String(this.context.favoriteEventsEndpoint || '').trim();
      this.favoriteCountCache = this.readFavoriteCountCache();
      this.favoriteCounts = new Map(
        Object.entries(this.favoriteCountCache).map(([key, entry]) => [key, entry.count]),
      );
      this.favoriteCountsLoadedFor = '';
      this.favoriteCountsTimer = null;
      this.favoriteCountsRetryAt = 0;
      this.visitorId = getVisitorId();
      this.syncTimer = null;
      this.items = mergeItems(readStoredItems(), this.context.customerFavorites || []);
      writeStoredItems(this.items);
      this.bind();
      this.renderAll();
      this.loadAccountFavorites();
    }

    readFavoriteCountCache() {
      try {
        const parsed = JSON.parse(window.sessionStorage.getItem(COUNT_CACHE_KEY) || '{}');
        const now = Date.now();
        return Object.fromEntries(Object.entries(parsed || {}).filter(([, entry]) => (
          Number.isFinite(Number(entry?.count)) && Number(entry?.expiresAt) > now
        )));
      } catch (error) {
        return {};
      }
    }

    writeFavoriteCountCache() {
      try {
        window.sessionStorage.setItem(COUNT_CACHE_KEY, JSON.stringify(this.favoriteCountCache));
      } catch (error) {
        // Favoriter fortsätter fungera även om webbläsaren blockerar sessionslagring.
      }
    }

    cacheFavoriteCount(key, count) {
      this.favoriteCountCache[key] = {
        count,
        expiresAt: Date.now() + COUNT_CACHE_TTL_MS,
      };
      this.favoriteCounts.set(key, count);
      this.writeFavoriteCountCache();
    }

    hasFreshFavoriteCount(key) {
      const entry = this.favoriteCountCache[key];
      if (entry && Number(entry.expiresAt) > Date.now()) return true;
      if (entry) {
        delete this.favoriteCountCache[key];
        this.favoriteCounts.delete(key);
        this.writeFavoriteCountCache();
      }
      return false;
    }

    invalidateFavoriteCount(key) {
      delete this.favoriteCountCache[key];
      this.favoriteCountsLoadedFor = '';
      this.writeFavoriteCountCache();
    }

    async fetchWithTimeout(url, options = {}, timeoutMs = 2500) {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await window.fetch(url, { ...options, signal: controller.signal });
      } finally {
        window.clearTimeout(timer);
      }
    }

    bind() {
      document.addEventListener('click', (event) => {
        document.querySelectorAll('.kickis-header-account[open]').forEach((accountMenu) => {
          if (!accountMenu.contains(event.target)) {
            accountMenu.removeAttribute('open');
          }
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        document.querySelectorAll('.kickis-header-account[open]').forEach((accountMenu) => {
          accountMenu.removeAttribute('open');
          accountMenu.querySelector('summary')?.focus();
        });
      });

      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-favorite-toggle]');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        this.toggleButton(button);
      });

      document.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-favorite-remove]');
        if (!removeButton) return;
        event.preventDefault();
        event.stopPropagation();
        this.remove(removeButton.dataset.favoriteKey);
      });

      document.addEventListener('click', (event) => {
        const card = event.target.closest('[data-favorite-card]');
        if (!card || event.target.closest('a, button')) return;
        const url = card.dataset.favoriteUrl;
        if (!url || url === '#') return;
        window.location.href = url;
      });

      window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY) return;
        this.items = readStoredItems();
        this.renderAll();
      });
    }

    toggleButton(button) {
      const item = normaliseItem({
        type: button.dataset.favoriteType,
        id: button.dataset.favoriteId,
        handle: button.dataset.favoriteHandle,
        url: button.dataset.favoriteUrl,
        title: button.dataset.favoriteTitle,
        image: button.dataset.favoriteImage,
        vendor: button.dataset.favoriteVendor,
        price: button.dataset.favoritePrice,
        compareAtPrice: button.dataset.favoriteCompareAtPrice,
        badges: String(button.dataset.favoriteBadges || '').split('|').map((badge) => badge.trim()).filter(Boolean),
      });
      const key = itemKey(item);
      const exists = this.items.some((existing) => itemKey(existing) === key);
      this.items = exists ? this.items.filter((existing) => itemKey(existing) !== key) : mergeItems([{ ...item, addedAt: new Date().toISOString() }], this.items);
      this.persist();
      this.adjustFavoriteCount(item, exists ? -1 : 1);
      this.recordFavoriteEvent(exists ? 'remove' : 'add', item);
      this.flash(button, exists ? 'Borttagen' : 'Sparad');
    }

    remove(key) {
      const removedItem = this.items.find((item) => itemKey(item) === key);
      this.items = this.items.filter((item) => itemKey(item) !== key);
      this.persist();
      if (removedItem) {
        this.adjustFavoriteCount(removedItem, -1);
        this.recordFavoriteEvent('remove', removedItem);
      }
    }

    persist() {
      writeStoredItems(this.items);
      this.renderAll();
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items: this.items } }));
      this.scheduleAccountSync();
    }

    async loadAccountFavorites() {
      if (!this.context.customerId || !this.syncEndpoint) return;

      try {
        const endpoint = new URL(this.syncEndpoint, window.location.origin);
        if (this.visitorId) endpoint.searchParams.set('visitor_id', this.visitorId);
        const response = await window.fetch(`${endpoint.pathname}${endpoint.search}`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        const accountItems = Array.isArray(payload?.favorites) ? payload.favorites.map(normaliseItem) : [];
        const browserItems = readStoredItems();
        this.items = mergeItems(accountItems, browserItems);
        writeStoredItems(this.items);
        this.renderAll();

        const accountKeys = new Set(accountItems.map(itemKey));
        const browserOnlyItems = browserItems.filter((item) => !accountKeys.has(itemKey(item)));
        for (const item of browserOnlyItems.slice(0, 40)) {
          await this.recordFavoriteEvent('add', item);
        }
      } catch (error) {
        console.warn('Kunde inte synka favoriter från kontot.', error);
      }
    }

    scheduleAccountSync() {
      if (!this.syncWritable) return;
      window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => this.saveAccountFavorites(), 450);
    }

    async saveAccountFavorites() {
      if (!this.syncWritable) return;

      try {
        const response = await window.fetch(this.syncEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ favorites: this.items }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      } catch (error) {
        console.warn('Kunde inte spara favoriter på kontot.', error);
      }
    }

    flash(button, text) {
      const label = button.querySelector('[data-favorite-button-label]');
      if (!label) return;
      const original = label.textContent;
      label.textContent = text;
      window.setTimeout(() => {
        label.textContent = original;
      }, 1200);
    }

    renderAll() {
      this.updateButtons();
      this.updateCounts();
      this.renderPage();
      this.queueFavoriteCountsLoad();
    }

    getCountableItems() {
      const fromButtons = Array.from(document.querySelectorAll('[data-favorite-toggle]')).map((button) => normaliseItem({
        type: button.dataset.favoriteType,
        id: button.dataset.favoriteId,
        handle: button.dataset.favoriteHandle,
        url: button.dataset.favoriteUrl,
        title: button.dataset.favoriteTitle,
        image: button.dataset.favoriteImage,
        vendor: button.dataset.favoriteVendor,
      }));

      const byKey = new Map();
      [...fromButtons, ...this.items].forEach((item) => {
        const key = itemKey(item);
        if (key) byKey.set(key, { ...item, key });
      });
      return Array.from(byKey.values());
    }

    queueFavoriteCountsLoad() {
      if (!this.favoriteCountsEnabled || !this.favoriteCountsEndpoint || Date.now() < this.favoriteCountsRetryAt) {
        this.updateFavoriteCountElements();
        return;
      }

      const items = this.getCountableItems().filter((item) => !this.hasFreshFavoriteCount(item.key));
      const signature = items.map((item) => item.key).sort().join('|');
      if (!signature || signature === this.favoriteCountsLoadedFor) {
        this.updateFavoriteCountElements();
        return;
      }

      this.favoriteCountsLoadedFor = signature;
      window.clearTimeout(this.favoriteCountsTimer);
      this.favoriteCountsTimer = window.setTimeout(() => this.loadFavoriteCounts(items), 100);
    }

    async loadFavoriteCounts(items = this.getCountableItems()) {
      if (!this.favoriteCountsEnabled || !this.favoriteCountsEndpoint || !items.length) return;

      try {
        const response = await this.fetchWithTimeout(this.favoriteCountsEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            visitorId: this.visitorId,
            keys: items.map((item) => this.countPayloadItem(item)),
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        this.applyFavoriteCounts(payload?.counts || payload, new Set(items.map((item) => item.key)));
        this.favoriteCountsLoadedFor = '';
      } catch (error) {
        items.forEach((item) => {
          this.favoriteCounts.delete(item.key);
          delete this.favoriteCountCache[item.key];
        });
        this.writeFavoriteCountCache();
        this.favoriteCountsRetryAt = Date.now() + 30_000;
        this.favoriteCountsLoadedFor = '';
        console.warn('Kunde inte läsa global favoritstatistik.', error);
      } finally {
        this.updateFavoriteCountElements();
      }
    }

    countPayloadItem(item) {
      return {
        key: item.key || itemKey(item),
        type: item.type || 'product',
        id: item.id || '',
        handle: item.handle || '',
        url: item.url || '',
        title: item.title || '',
        imageUrl: item.image || '',
      };
    }

    applyFavoriteCounts(counts, allowedKeys = null) {
      const acceptsKey = (key) => !allowedKeys || allowedKeys.has(key);

      if (Array.isArray(counts)) {
        counts.forEach((entry) => {
          const key = entry?.key || itemKey(entry || {});
          const count = toCount(entry?.count);
          if (key && acceptsKey(key) && count !== null) this.cacheFavoriteCount(key, count);
        });
        return;
      }

      Object.entries(counts || {}).forEach(([key, value]) => {
        const count = toCount(value?.count ?? value);
        if (key && acceptsKey(key) && count !== null) this.cacheFavoriteCount(key, count);
      });
    }

    adjustFavoriteCount(item, delta) {
      const key = itemKey(item);
      if (!key) return;
      this.invalidateFavoriteCount(key);
      const current = toCount(this.favoriteCounts.get(key));
      if (current === null) {
        if (delta > 0) this.favoriteCounts.set(key, 1);
        this.updateFavoriteCountElements();
        return;
      }
      this.favoriteCounts.set(key, Math.max(0, current + delta));
      this.updateFavoriteCountElements();
    }

    visibleFavoriteCount(key) {
      const backendCount = toCount(this.favoriteCounts.get(key));
      const isLocallyActive = this.items.some((item) => itemKey(item) === key);
      if (isLocallyActive) return Math.max(1, backendCount ?? 0);
      return backendCount ?? undefined;
    }

    async recordFavoriteEvent(action, item) {
      if (!this.favoriteCountsEnabled || !this.favoriteEventsEndpoint) return;

      try {
        const response = await this.fetchWithTimeout(this.favoriteEventsEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            visitorId: this.visitorId,
            action,
            item: this.countPayloadItem(item),
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json().catch(() => ({}));
        if (payload?.counts || payload?.count !== undefined) {
          this.applyFavoriteCounts(payload.counts || { [itemKey(item)]: payload.count });
          this.updateFavoriteCountElements();
        }
      } catch (error) {
        this.favoriteCounts.delete(itemKey(item));
        this.updateFavoriteCountElements();
        console.warn('Kunde inte spara global favoritstatistik.', error);
      }
    }

    updateFavoriteCountElements() {
      document.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
        const key = itemKey({
          type: button.dataset.favoriteType,
          id: button.dataset.favoriteId,
          handle: button.dataset.favoriteHandle,
          url: button.dataset.favoriteUrl,
        });
        const count = this.visibleFavoriteCount(key);
        const countElement = button.querySelector('[data-favorite-count-for]');
        if (!countElement) return;

        if (count === undefined || count < 1) {
          countElement.hidden = true;
          countElement.textContent = '';
          return;
        }

        countElement.hidden = false;
        countElement.textContent = String(count);
        countElement.setAttribute('aria-label', formatCount(count));
      });

      document.querySelectorAll('[data-favorite-card-count]').forEach((element) => {
        const count = this.visibleFavoriteCount(element.dataset.favoriteKey);
        if (count === undefined || count < 1) {
          element.hidden = true;
          element.textContent = '';
          return;
        }

        element.hidden = false;
        element.textContent = formatCount(count);
      });

      document.querySelectorAll('[data-product-favorite-count]').forEach((element) => {
        const key = element.dataset.favoriteKey;
        const count = this.visibleFavoriteCount(key);
        element.hidden = count === undefined || count < 1;
        element.textContent = count === undefined || count < 1 ? '' : formatCount(count);
      });
    }

    updateButtons() {
      const keys = new Set(this.items.map(itemKey));
      document.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
        const key = itemKey({
          type: button.dataset.favoriteType,
          id: button.dataset.favoriteId,
          handle: button.dataset.favoriteHandle,
          url: button.dataset.favoriteUrl,
        });
        const active = keys.has(key);
        const count = this.visibleFavoriteCount(key);
        const countText = count === undefined ? '' : `, ${formatCount(count)}`;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${active ? 'Ta bort' : 'Lägg till'} ${button.dataset.favoriteTitle || 'favorit'} ${active ? 'från' : 'i'} favoriter${countText}`);
      });
    }

    updateCounts() {
      const count = this.items.length;
      document.querySelectorAll('[data-favorite-total-count]').forEach((element) => {
        element.textContent = String(count);
      });
      document.querySelectorAll('[data-favorite-count-bubble]').forEach((element) => {
        element.hidden = count === 0;
      });
    }

    renderPage() {
      const grid = document.querySelector('[data-favorites-grid]');
      if (!grid) return;

      const empty = document.querySelector('[data-favorites-empty]');
      const products = this.items.filter((item) => item.type === 'product');
      const patterns = this.items.filter((item) => item.type === 'pattern');

      if (empty) empty.hidden = this.items.length > 0;
      grid.hidden = this.items.length === 0;

      if (!this.items.length) {
        grid.innerHTML = '';
        return;
      }

      grid.innerHTML = [
        this.renderGroup('Produkter', products),
        this.renderGroup('Mönster', patterns),
        !patterns.length ? this.renderMissingGroup(
          'Fortsätt att fylla på med dina favoriter',
          'Du har redan sparat produkter. Lägg gärna till mönster också, så hittar du snabbt tillbaka till både inspiration och material.',
        ) : '',
        !products.length ? this.renderMissingGroup(
          'Fortsätt att fylla på med dina favoriter',
          'Du har redan sparat mönster. Lägg gärna till produkter också, så hittar du snabbt tillbaka till garn, stickor och tillbehör.',
        ) : '',
      ].filter(Boolean).join('');
    }

    renderGroup(title, items) {
      if (!items.length) return '';
      return `
        <section class="kickis-favorites-section">
          <h2 class="kickis-favorites-section__title">${escapeHtml(title)}</h2>
          <div class="kickis-favorites-grid__items">
            ${items.map((item) => this.renderCard(item)).join('')}
          </div>
        </section>
      `;
    }

    renderMissingGroup(title, text) {
      const addMoreText = 'Gå till produkt- eller mönstersidan för att lägga till fler favoriter.';
      return `
        <section class="kickis-favorites-section kickis-favorites-section--empty">
          <h2 class="kickis-favorites-section__title">${escapeHtml(title)}</h2>
          <p class="kickis-favorites-section__empty-text">${escapeHtml(addMoreText || text)}</p>
        </section>
      `;
    }

    renderCard(item) {
      const key = itemKey(item);
      const badges = item.badges.map((badge) => `<span class="kickis-card-label">${escapeHtml(badge)}</span>`).join('');
      const url = item.url || '#';
      return `
        <article class="kickis-favorite-card" data-favorite-card data-favorite-url="${escapeHtml(url)}">
          <a class="kickis-favorite-card__media" href="${escapeHtml(url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : '<span class="kickis-favorite-card__placeholder">♡</span>'}
            ${badges ? `<span class="kickis-favorite-card__badges">${badges}</span>` : ''}
          </a>
          <div class="kickis-favorite-card__body">
            <p class="kickis-favorite-card__type">${escapeHtml(item.type === 'pattern' ? 'Mönster' : item.vendor || 'Produkt')}</p>
            <h3 class="kickis-favorite-card__title"><a href="${escapeHtml(url)}">${escapeHtml(item.title)}</a></h3>
            ${item.price ? `<p class="kickis-favorite-card__price">${escapeHtml(item.price)}</p>` : ''}
            <p class="kickis-favorite-card__social" data-favorite-card-count data-favorite-key="${escapeHtml(key)}" hidden></p>
            <button type="button" class="button button--secondary kickis-favorite-card__remove" data-favorite-remove data-favorite-key="${escapeHtml(key)}">Ta bort</button>
          </div>
        </article>
      `;
    }
  }

  const start = () => {
    window.KickisFavorites = new KickisFavorites();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
